import React from 'react';
import { Text, View, StyleSheet } from '@react-pdf/renderer';
import { formatNumber } from 'utils/formatNumbers';
import { calculateLost } from 'utils/calculateLost';
import {
  returnRate,
  returnRateEach,
} from 'components/modules/EditHarvestSummaryModal/utils';

// Create styles
const styles = StyleSheet.create({
  row: {
    width: '100%',
    flexDirection: 'row',
  },
  cell: {
    backgroundColor: '#F9F4DA',
    color: '#53575A',
    fontFamily: 'Trebuchet',
    width: 50,
    height: 30,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 10,
    borderRight: '1px solid #BABABA',
  },
  cellSmallFont: {
    fontSize: 9, // 1px smaller for large values
  },
});

// Helper function to check if value needs smaller font
const needsSmallFont = (value: number): boolean => {
  return Math.abs(value) >= 1000000; // 7+ digits
};

// Create Document Component
const ItemDetailsRow = ({
  isTotal = false,
  isOdd = false,
  row,
}: {
  isTotal?: boolean | undefined;
  isOdd?: boolean | undefined;
  row: any;
}) => {
  if (!row) return null;
  let lost = calculateLost(row);
  let used = row.ret + lost;

  return (
    <>
      {!isTotal && (
        <View style={styles.row}>
          <View
            style={{
              ...styles.cell,
              width: 72,
              backgroundColor: `${
                isTotal ? '#ECE5C3' : isOdd ? '#F9F4DA' : '#FFFCE8'
              }`,
            }}>
            <Text>{row.sku?.name}</Text>
          </View>
          <View
            style={{
              ...styles.cell,
              backgroundColor: `${
                isTotal ? '#ECE5C3' : isOdd ? '#F9F4DA' : '#FFFCE8'
              }`,
            }}>
            <Text style={needsSmallFont(row.beg) ? styles.cellSmallFont : {}}>
              {formatNumber(row.beg)}
            </Text>
          </View>
          <View
            style={{
              ...styles.cell,
              backgroundColor: `${
                isTotal ? '#ECE5C3' : isOdd ? '#F9F4DA' : '#FFFCE8'
              }`,
            }}>
            <Text style={needsSmallFont(row.del) ? styles.cellSmallFont : {}}>
              {formatNumber(row.del)}
            </Text>
          </View>
          <View
            style={{
              ...styles.cell,
              backgroundColor: `${
                isTotal ? '#ECE5C3' : isOdd ? '#F9F4DA' : '#FFFCE8'
              }`,
            }}>
            <Text style={needsSmallFont(row.ret) ? styles.cellSmallFont : {}}>
              {formatNumber(row.ret)}
            </Text>
          </View>
          <View
            style={{
              ...styles.cell,
              backgroundColor: `${
                isTotal ? '#ECE5C3' : isOdd ? '#F9F4DA' : '#FFFCE8'
              }`,
            }}>
            <Text style={needsSmallFont(row.unu) ? styles.cellSmallFont : {}}>
              {formatNumber(row.unu)}
            </Text>
          </View>
          <View
            style={{
              ...styles.cell,
              backgroundColor: `${
                isTotal ? '#ECE5C3' : isOdd ? '#F9F4DA' : '#FFFCE8'
              }`,
            }}>
            <Text style={needsSmallFont(row.end) ? styles.cellSmallFont : {}}>
              {formatNumber(row.end)}
            </Text>
          </View>
          <View
            style={{
              ...styles.cell,
              width: 72,
              backgroundColor: `${
                isTotal ? '#E7A9B0' : isOdd ? '#E9C3C7' : '#F6D7DA'
              }`,
            }}>
            <Text>{formatNumber(lost)}</Text>
          </View>
          <View
            style={{
              ...styles.cell,
              width: 72,
              backgroundColor: `${
                isTotal ? '#9CC5E7' : isOdd ? '#AECFEA' : '#BFDCF3'
              }`,
            }}>
            <Text> {formatNumber(used)}</Text>
          </View>
          <View
            style={{
              ...styles.cell,
              width: 72,
              backgroundColor: `${
                isTotal ? '#9BDA98' : isOdd ? '#BAE4B8' : '#DCFADA'
              }`,
              borderRight: 0,
            }}>
            <Text>
              {isNaN(returnRateEach(row)) ? 0 : returnRateEach(row).toFixed(2)}%
            </Text>
          </View>
        </View>
      )}
      {/* totals */}
      {isTotal && (
        <View style={[styles.row, { fontWeight: 'bold' }]}>
          <View
            style={{
              ...styles.cell,
              width: 72,
              backgroundColor: `${
                isTotal ? '#ECE5C3' : isOdd ? '#F9F4DA' : '#FFFCE8'
              }`,
            }}>
            <Text>Total</Text>
          </View>
          <View
            style={{
              ...styles.cell,
              backgroundColor: `${
                isTotal ? '#ECE5C3' : isOdd ? '#F9F4DA' : '#FFFCE8'
              }`,
            }}>
            <Text
              style={
                needsSmallFont(row?.beginning) ? styles.cellSmallFont : {}
              }>
              {row?.beginning ? formatNumber(row.beginning) : ''}
            </Text>
          </View>
          <View
            style={{
              ...styles.cell,
              backgroundColor: `${
                isTotal ? '#ECE5C3' : isOdd ? '#F9F4DA' : '#FFFCE8'
              }`,
            }}>
            <Text
              style={
                needsSmallFont(row?.totalDelivered) ? styles.cellSmallFont : {}
              }>
              {row?.totalDelivered ? formatNumber(row.totalDelivered) : ''}
            </Text>
          </View>
          <View
            style={{
              ...styles.cell,
              backgroundColor: `${
                isTotal ? '#ECE5C3' : isOdd ? '#F9F4DA' : '#FFFCE8'
              }`,
            }}>
            <Text
              style={
                needsSmallFont(row?.returnedDirty) ? styles.cellSmallFont : {}
              }>
              {row?.returnedDirty ? formatNumber(row.returnedDirty) : ''}
            </Text>
          </View>
          <View
            style={{
              ...styles.cell,
              backgroundColor: `${
                isTotal ? '#ECE5C3' : isOdd ? '#F9F4DA' : '#FFFCE8'
              }`,
            }}>
            <Text
              style={
                needsSmallFont(row?.returnedUnused) ? styles.cellSmallFont : {}
              }>
              {row?.returnedUnused ? formatNumber(row.returnedUnused) : ''}
            </Text>
          </View>
          <View
            style={{
              ...styles.cell,
              backgroundColor: `${
                isTotal ? '#ECE5C3' : isOdd ? '#F9F4DA' : '#FFFCE8'
              }`,
            }}>
            <Text
              style={needsSmallFont(row?.ending) ? styles.cellSmallFont : {}}>
              {row?.ending ? formatNumber(row.ending) : ''}
            </Text>
          </View>
          <View
            style={{
              ...styles.cell,
              width: 72,
              backgroundColor: `${isOdd ? '#E9C3C7' : '#F6D7DA'}`,
            }}>
            <Text>{row?.lost ? formatNumber(row.lost) : ''}</Text>
          </View>
          <View
            style={{
              ...styles.cell,
              width: 72,
              backgroundColor: `${isOdd ? '#AECFEA' : '#BFDCF3'}`,
            }}>
            <Text>{row?.used ? formatNumber(row.used) : ''}</Text>
          </View>
          <View
            style={{
              ...styles.cell,
              width: 72,
              borderRight: 0,
              backgroundColor: `${isOdd ? '#BAE4B8' : '#DCFADA'}`,
            }}>
            <Text>{returnRate(row).toFixed(2)}%</Text>
          </View>
        </View>
      )}
    </>
  );
};

export default ItemDetailsRow;
